import axios from 'axios';
import { STANDARD_BRAND, LOW_STANDARD_BRAND } from 'lion-vue-src/config/brand';
import { STANDARD_RETRY_STATES } from '../config';

const FAILED_REGISTRATION_MESSAGE = 'Oops, something went wrong with your brand registration. Please email Support@liondesk.com to report your issue.';

const initialState = {
  brandForm: {
    textVolume: '',
  },
  campaignForm: {},
  currentStep: 0,
  successRegistration: false,
  failedRegistration: false,
  registrationMessage: '',
  isLoading: false,
  preFilledForm: false,
};

const getRegistrationPayload = (brandForm, campaignForm) => {
  const publicCompanyType = 'public';
  const countryCode = '+1';
  let payload = {
    'business_information': {
      'business_name': brandForm.businessName,
      'web_site_url': brandForm.website,
      'business_regions_of_operation': brandForm.businessRegion.join(','),
      'business_type': brandForm.businessType,
      'business_registration_identifier': brandForm.businessRegistration,
      'business_identity': 'direct_customer',
      'business_industry': brandForm.businessIndustry,
      'business_registration_number': brandForm.businessRegistrationEIN,
    },
    'authorized_representative': {
      'job_position': brandForm.jobPosition,
      'last_name': brandForm.lastName,
      'phone_number': `${countryCode}${brandForm.phone}`,
      'first_name': brandForm.firstName,
      'email': brandForm.email,
      'business_title': brandForm.businessTitle,
    },
    'address': {
      'street': brandForm.street,
      'city': brandForm.city,
      'region': brandForm.state,
      'postal_code': brandForm.zipCode,
      'iso_country': brandForm.country,
    },
    'company': {
      'company_type': brandForm.companyType,
    },
    'campaign': {
      'campaign_description': campaignForm.useCase,
      'message_sample_1': campaignForm.sample1,
      'message_sample_2': campaignForm.sample2,
      'us_app_to_person_use_case': 'MIXED',
      'has_embedded_links': true,
      'has_embedded_phone': true
    }
  };

  if (brandForm.companyType === publicCompanyType) {
    payload.company['stock_exchange'] = brandForm.stockExchangeType;
    payload.company['stock_ticker'] = brandForm.stockSymbol;
  }

  return payload;
};

const mapBrandForm = (data, registrationType = STANDARD_BRAND) => {
  const countryCode = '+1';
  const {
    business_information: businessInformation,
    authorized_representative: authorizedRepresentative,
    address,
    company,
  } = data;
  const brandForm = {
    businessName: businessInformation.business_name,
    website: businessInformation.web_site_url,
    businessRegion: businessInformation.business_regions_of_operation.split(','),
    businessType: businessInformation.business_type,
    businessRegistration: businessInformation.business_registration_identifier,
    businessIndustry: businessInformation.business_industry,
    businessRegistrationEIN: businessInformation.business_registration_number,
    jobPosition: authorizedRepresentative.job_position,
    lastName: authorizedRepresentative.last_name,
    phone: authorizedRepresentative.phone_number.replace(countryCode, ''),
    firstName: authorizedRepresentative.first_name,
    email: authorizedRepresentative.email,
    businessTitle: authorizedRepresentative.business_title,
    street: address.street,
    city: address.city,
    state: address.region,
    zipCode: address.postal_code,
    country: address.iso_country,
    companyType: company.company_type,
    stockExchangeType: company.stock_exchange,
    stockSymbol: company.stock_ticker,
    textVolume: registrationType,
  };
  return brandForm;
};

const mapCampaignForm = data => {
  const { campaign } = data;
  const campaignForm = {
    useCase: campaign.campaign_description,
    sample1: campaign.message_sample_1,
    sample2: campaign.message_sample_2,
  };
  return campaignForm;
};

export default {
  namespaced: true,
  state: { ...initialState },
  getters: {
    isBrandFormValid(state) {
      return Object.keys(state.brandForm).length > 0;
    },
    isStandardRegistration(state) {
      return state.brandForm.textVolume === STANDARD_BRAND;
    },
  },
  mutations: {
    setBrandForm(store, { form }) {
      store.brandForm = form;
    },
    setCampaignForm(store, { form }) {
      store.campaignForm = form;
    },
    setPreFilledForm(store, bool) {
      store.preFilledForm = bool
    },
    setStep(store, { step }) {
      store.currentStep = step;
    },
    setSuccessRegistration(store, value) {
      store.successRegistration = value;
    },
    setRegistrationMessage(store, message) {
      store.registrationMessage = message;
    },
    setFailedRegistration(store, message) {
      store.failedRegistration = message;
    },
    setIsLoading(store, value) {
      store.isLoading = value;
    },
    setTextVolume(store, value) {
      store.brandForm.textVolume = value;
    },
  },
  actions: {
    async sendStandardRegistration({ rootState, state, commit }) {
      commit('setIsLoading', true);
      try {
        const { textVolume } = state.brandForm;
        const registerType = textVolume.toLowerCase();
        const payload = getRegistrationPayload(state.brandForm, state.campaignForm);
        let reqMethod = 'POST';
        if (STANDARD_RETRY_STATES.includes(rootState.conversations.registrationStatus10dlc)) {
          reqMethod = 'PUT';
        }
        await axios({
          url: `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/register-10dlc/${registerType}`,
          method: reqMethod,
          data: payload,
        });
        commit('setSuccessRegistration', true);
      } catch (error) {
        commit('setFailedRegistration', true);
        commit('setRegistrationMessage', error.data.error || FAILED_REGISTRATION_MESSAGE);
      } finally {
        commit('setIsLoading', false);
      }
    },
    preFillForm({ state, commit, rootState }) {
      const { registrationData10dlc, registrationType10dlc } = rootState.conversations;
      if (!registrationData10dlc) {
        return
      }
      if (registrationType10dlc === LOW_STANDARD_BRAND || registrationType10dlc === STANDARD_BRAND) {
        const brandForm = mapBrandForm(registrationData10dlc, registrationType10dlc);
        const campaignForm = mapCampaignForm(registrationData10dlc);
        commit('setBrandForm', { form: brandForm });
        commit('setCampaignForm', { form: campaignForm });
        commit('setPreFilledForm', true)
      }
    },
  },
};
